import { useState, useEffect } from 'react'
import { useT } from '../../hooks/useTranslation'
import { getPositions, clearDiary } from '../../services/api'
import { botApi } from '../../services/botApi'
import { RefreshCw, Trash2 } from 'lucide-react'
import ConfirmModal from '../common/ConfirmModal'
import { formatPrice, formatPnlNoRound } from '../../utils/formatCurrency'

function MetaItem({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, color: '#636366', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 12, color: valueColor ?? '#8E8E93', fontWeight: 500 }}>{value}</span>
    </div>
  )
}

// Компонент сводки: общий доход и сумма ROI по марже всех сделок
function SummaryCard({ positions, t }) {
  // P&L как на бирже: 2 знака после запятой БЕЗ округления второго
  // (formatPnlNoRound) — на любой вкладке (Реальный и Демо/Пробный).
  const totalPnl = positions.reduce((sum, p) => sum + p.pnl, 0)

  // Считаем roiMargin для каждой сделки той же логикой, что и в PositionCard,
  // и суммируем — чтобы итог совпадал с процентами, показанными рядом с PnL на карточках.
  const totalRoiMargin = positions.reduce((sum, p) => {
    const notional = p.amount ?? p.notional ??
      (p.size != null && p.entry != null ? p.size * p.entry : null)
    const margin = p.margin ??
      (notional != null && p.leverage ? notional / p.leverage : null)
    const roiMargin = p.roiMargin != null
      ? p.roiMargin
      : (margin ? (p.pnl / margin * 100) : 0)
    return sum + roiMargin
  }, 0)

  const isProfit = totalPnl >= 0

  return (
    <div style={{
      background: '#1A1A1A',
      borderRadius: 14,
      padding: '14px 16px',
      marginBottom: 16,
      border: '1px solid rgba(255,255,255,0.04)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      {/* Общий доход */}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#636366', fontWeight: 500, marginBottom: 4 }}>
          {t.diaryTotalIncome}
        </div>
        <div style={{
          fontSize: 20,
          fontWeight: 700,
          color: isProfit ? '#34D399' : '#FF3B30',
        }}>
          {formatPnlNoRound(totalPnl)}
        </div>
      </div>

      {/* Разделитель */}
      <div style={{
        width: 1,
        height: 40,
        background: 'rgba(255,255,255,0.06)',
        margin: '0 16px',
      }} />

      {/* Общий процент (сумма ROI по марже всех сделок) */}
      <div style={{ flex: 1, textAlign: 'right' }}>
        <div style={{ fontSize: 11, color: '#636366', fontWeight: 500, marginBottom: 4 }}>
          {t.diaryTotalPct}
        </div>
        <div style={{
          fontSize: 20,
          fontWeight: 700,
          color: totalRoiMargin >= 0 ? '#34D399' : '#FF3B30',
        }}>
          {totalRoiMargin >= 0 ? '+' : ''}{totalRoiMargin.toFixed(2)}%
        </div>
      </div>
    </div>
  )
}

function PositionCard({ position, t }) {
  const isLong    = position.side === 'long'
  const isProfit  = position.pnl >= 0
  const sideColor = isLong ? '#34D399' : '#FF3B30'
  const sideLabel = isLong ? t.diaryLong : t.diaryShort
  const pnlColor  = isProfit ? '#34D399' : '#FF3B30'
  // Без округления сотых (0.18689647 → «+$0.18», а не «+$0.19») — на любой
  // вкладке (Реальный и Демо/Пробный): цифра как на бирже, 2 знака.
  const pnlText   = formatPnlNoRound(position.pnl)
  // PnL всегда ровно 2 знака после запятой («+$0.06»), строка короткая; на
  // случай очень больших сумм для длинных значений чуть уменьшаем шрифт.
  const pnlFontSize = pnlText.length > 11 ? 14 : 17
  const roi       = position.roi != null ? position.roi : (position.size ? (position.pnl / position.size * 100) : null)

  // ROI по марже. Приоритет — точные значения с бэкенда (ROI_MARGIN / MARGIN / AMOUNT),
  // и только если их нет — считаем сами.
  // ВАЖНО: position.size — номинал позиции в USDT (AMOUNT), НЕ количество монет.
  // margin = amount / leverage (плечо с бэкенда, реальное с биржи).
  const notional  = position.amount ?? position.notional ??
    (position.size != null && position.entry != null ? position.size * position.entry : null)
  const margin    = position.margin ??
    (notional != null && position.leverage ? notional / position.leverage : null)
  const roiMargin = position.roiMargin != null
    ? position.roiMargin
    : (margin ? (position.pnl / margin * 100) : null)

  // Индикатор статуса бота: зелёный = активный, красный = неактивный
  const botIndicatorColor = position.botActive ? '#34D399' : '#FF3B30'

  return (
    <div style={{
      background: '#1A1A1A',
      borderRadius: 16,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      border: '1px solid rgba(255,255,255,0.04)',
    }}>
      {/* Row 1: Pair + Side badge + PnL + ROI по марже (рядом с PnL) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{
            fontSize: 15, fontWeight: 700, color: '#FFFFFF',
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {position.pair}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, color: sideColor,
            background: isLong ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.12)',
            borderRadius: 6, padding: '2px 7px',
            letterSpacing: 0.3, flexShrink: 0,
          }}>
            {sideLabel}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: pnlFontSize, fontWeight: 700, color: pnlColor, whiteSpace: 'nowrap' }}>
            {pnlText}
          </span>
          {roiMargin != null && (
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: roiMargin >= 0 ? '#34D399' : '#FF3B30',
              background: roiMargin >= 0 ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.12)',
              borderRadius: 5, padding: '1px 6px',
            }}>
              {roiMargin >= 0 ? '+' : ''}{roiMargin.toFixed(2)}%
            </span>
          )}
        </div>
      </div>

      {/* Row 2: Bot name with indicator + leverage + exchange */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 7, flexWrap: 'wrap' }}>
        {(position.botName || position.virtual) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', flex: 1 }}>
            {/* Пробный виртуальный бот: бейдж «ПРОБНЫЙ» перенесли в строку
                биржи/плеча — не заслоняет название монеты. */}
            {position.virtual && (
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#FFB020',
                background: 'rgba(255,176,32,0.12)',
                border: '1px solid rgba(255,176,32,0.3)',
                borderRadius: 5, padding: '2px 6px',
                letterSpacing: 0.5, flexShrink: 0,
              }}>
                {t.virtualBadge}
              </span>
            )}
            <span style={{ fontSize: 14, fontWeight: 700, color: '#34D399', letterSpacing: 0.1 }}>
              {position.botName}
            </span>
            {position.leverage && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#8E8E93',
                background: 'rgba(255,255,255,0.07)',
                borderRadius: 5, padding: '1px 6px',
              }}>
                {position.leverage}×
              </span>
            )}
            {position.exchange && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#636366',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 5, padding: '1px 6px',
              }}>
                {position.exchange}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'rgba(255,255,255,0.05)' }} />

      {/* Info row 1: Entry / Exit / Closed */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
        <MetaItem label={t.diaryEntry}    value={`$${formatPrice(position.entry)}`} />
        <MetaItem label={t.diaryExit}     value={`$${formatPrice(position.exit)}`} />
        <MetaItem
          label={t.diaryClosedAt}
          value={position.closedAt ? position.closedAt.slice(11, 19) : '—'}
        />
      </div>

      {/* Info row 2: Volume / ROI / Funding */}
      {(position.size != null || roi != null || position.funding != null) && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4,
          paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          {position.size != null && (
            <MetaItem label={t.diarySize} value={`$${Number(position.size).toFixed(2)}`} />
          )}
          {roi != null && (
            <MetaItem
              label={t.diaryRoi}
              value={`${roi >= 0 ? '+' : ''}${roi.toFixed(2)}%`}
              valueColor={roi >= 0 ? '#34D399' : '#FF3B30'}
            />
          )}
          {position.funding != null && (
            <MetaItem
              label={t.funding}
              value={`${position.funding >= 0 ? '+' : ''}${position.funding.toFixed(4)}%`}
              valueColor={position.funding >= 0 ? '#34D399' : '#FF3B30'}
            />
          )}
        </div>
      )}
    </div>
  )
}

function DateGroup({ date, positions, t }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontSize: 12, color: '#636366', fontWeight: 600, marginBottom: 8, marginLeft: 2 }}>
        {date}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {positions.map(pos => (
          <PositionCard key={pos.id} position={pos} t={t} />
        ))}
      </div>
    </div>
  )
}

export default function DiaryPage() {
  const t = useT()
  const [accountMode, setAccountMode] = useState('real') // 'real' | 'demo'
  const [positions, setPositions] = useState([])
  const [bots, setBots] = useState([])
  const [isSyncing, setIsSyncing] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      await botApi.syncDiary()
      // После синхронизации обновляем данные
      const data = await getPositions({ limit: 100, demo: accountMode === 'demo' })
      if (data && data.length > 0) {
        setPositions(data)
      } else {
        setPositions([])
      }
    } catch (err) {
      console.error('[Diary] Sync failed:', err)
    } finally {
      setIsSyncing(false)
    }
  }

  useEffect(() => {
    getPositions({ limit: 100, demo: accountMode === 'demo' }).then(data => {
      if (data && data.length > 0) {
        setPositions(data)
      } else {
        setPositions([])
      }
    }).catch(() => {
      setPositions([])
    })

    // Загружаем список ботов
    botApi.getBots().then(data => {
      if (data && Array.isArray(data)) {
        setBots(data)
      } else {
        setBots([])
      }
    }).catch(() => {
      setBots([])
    })
  }, [accountMode])

  const grouped = positions.reduce((acc, pos) => {
    const date = pos.closedAt ? pos.closedAt.slice(0, 10) : '—'
    if (!acc[date]) acc[date] = []
    acc[date].push(pos)
    return acc
  }, {})

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a))

  const formatDate = iso => {
    const [y, m, d] = iso.split('-')
    return `${d}.${m}.${y}`
  }

  return (
    <div>
      {/* Переключатель Реальный / Демо */}
      <div style={{
        display: 'flex', background: '#1A1A1A',
        borderRadius: 14, padding: 4, gap: 2, marginBottom: 18,
      }}>
        <button
          onClick={() => setAccountMode('real')}
          style={{
            flex: 1, padding: '10px 6px', borderRadius: 10,
            fontSize: 13, fontWeight: 700,
            background: accountMode === 'real'
              ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
              : 'transparent',
            color: accountMode === 'real' ? '#fff' : '#636366',
            border: 'none', cursor: 'pointer', transition: 'all 0.2s',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t.real}
        </button>
        <button
          onClick={() => setAccountMode('demo')}
          style={{
            flex: 1, padding: '10px 6px', borderRadius: 10,
            fontSize: 13, fontWeight: 700,
            background: accountMode === 'demo'
              ? 'linear-gradient(135deg, #636366 0%, #48484A 100%)'
              : 'transparent',
            color: accountMode === 'demo' ? '#fff' : '#636366',
            border: 'none', cursor: 'pointer', transition: 'all 0.2s',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t.demo}
        </button>
      </div>

      {/* Панель действий: Обновить и Очистить */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        {/* Кнопка обновления (слева) */}
        <button
          onClick={handleSync}
          disabled={isSyncing}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isSyncing ? 'not-allowed' : 'pointer',
            opacity: isSyncing ? 0.5 : 0.6,
            padding: 0,
            transition: 'all 0.2s',
            WebkitTapHighlightColor: 'transparent',
          }}
          aria-label={t.refreshData}
        >
          <RefreshCw
            size={16}
            color="#636366"
            className={isSyncing ? 'animate-spin' : ''}
          />
        </button>

        {/* Кнопка очистки (справа) */}
        <button
          disabled={bots.length > 0}
          onClick={() => {
            if (bots.length === 0) {
              setShowClearConfirm(true)
            }
          }}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: bots.length > 0 ? 'not-allowed' : 'pointer',
            opacity: bots.length > 0 ? 0.3 : 0.6,
            padding: 0,
            transition: 'all 0.2s',
            WebkitTapHighlightColor: 'transparent',
          }}
          aria-label={t.diaryClearAction}
          title={bots.length > 0 ? t.diaryClearNeedDeleteBots : t.diaryClearAction}
        >
          <Trash2 size={16} color={bots.length > 0 ? '#636366' : '#FF3B30'} />
        </button>
      </div>

      {/* Сводка: общий доход и ROI */}
      <SummaryCard positions={positions} t={t} />

      {/* Список сделок */}
      <div style={{ paddingTop: 4 }}>
        {sortedDates.map(date => (
          <DateGroup key={date} date={formatDate(date)} positions={grouped[date]} t={t} />
        ))}
      </div>

      {/* Confirm modal for clearing diary */}
      {showClearConfirm && (
        <ConfirmModal
          title={t.diaryClearConfirmTitle}
          message={t.diaryClearConfirmMsg}
          confirmLabel={t.diaryClearAction}
          cancelLabel={t.diaryClearCancel}
          onConfirm={async () => {
            setShowClearConfirm(false)
            try {
              // Сначала очищаем состояние, чтобы интерфейс сразу "опустел"
              setPositions([]);
              // Вызываем очистку на сервере с правильным режимом
              await clearDiary(accountMode === 'demo');
              // Принудительно запрашиваем актуальные (пустые) данные с сервера
              const data = await getPositions({ limit: 100, demo: accountMode === 'demo' });
              setPositions(data || []);
            } catch (err) {
              console.error('[Diary] Clear failed:', err);
              // В случае ошибки можно попробовать перезагрузить данные
              const data = await getPositions({ limit: 100, demo: accountMode === 'demo' });
              setPositions(data || []);
            }
          }}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}
    </div>
  )
}